/**
 * A byte-budgeted LRU cache with pinning.
 *
 * Tiles are expensive to fetch and decode, so a tile that scrolls out of the
 * desired set is kept around rather than thrown away — walking twenty metres
 * back the way you came should not re-download anything. Entries are evicted
 * only when the total estimated byte count exceeds the budget.
 *
 * Pinning exists because "currently being drawn" and "recently used" are not
 * the same thing. A tile the renderer is holding must never be evicted no
 * matter how long ago it was touched, or the world develops holes.
 */

export interface LruOptions<T> {
  maxBytes: number;
  /** Estimated retained size of a value, in bytes. */
  sizeOf: (value: T) => number;
  /** Called for each evicted value, so callers can release GPU resources. */
  onEvict?: (key: string, value: T) => void;
}

interface Entry<T> {
  value: T;
  bytes: number;
  pinned: boolean;
}

export class LruCache<T> {
  /** Map iteration order is insertion order, which is what makes this an LRU:
   *  touching an entry deletes and reinserts it, moving it to the end. */
  private readonly entries = new Map<string, Entry<T>>();
  private readonly options: LruOptions<T>;
  private totalBytes = 0;

  constructor(options: LruOptions<T>) {
    this.options = options;
  }

  get bytes(): number {
    return this.totalBytes;
  }

  get size(): number {
    return this.entries.size;
  }

  get pinnedBytes(): number {
    let n = 0;
    for (const entry of this.entries.values()) if (entry.pinned) n += entry.bytes;
    return n;
  }

  has(key: string): boolean {
    return this.entries.has(key);
  }

  /** Look up and mark as most recently used. */
  get(key: string): T | undefined {
    const entry = this.entries.get(key);
    if (entry === undefined) return undefined;
    this.entries.delete(key);
    this.entries.set(key, entry);
    return entry.value;
  }

  /** Look up without affecting recency. */
  peek(key: string): T | undefined {
    return this.entries.get(key)?.value;
  }

  /**
   * Insert or replace an entry.
   *
   * Pass `pin: true` when the caller is about to use the value. Pinning has to
   * be part of the insert rather than a follow-up call: an over-budget `set`
   * trims immediately, and the entry just written is the newest and therefore
   * the only unpinned candidate — so a set-then-pin sequence can evict exactly
   * the value the caller was storing, leaving `pin` to no-op on a missing key.
   */
  set(key: string, value: T, options?: { pin?: boolean }): void {
    const existing = this.entries.get(key);
    const bytes = this.options.sizeOf(value);
    if (existing !== undefined) {
      this.totalBytes -= existing.bytes;
      this.entries.delete(key);
      if (existing.value !== value) this.options.onEvict?.(key, existing.value);
    }
    this.entries.set(key, {
      value,
      bytes,
      pinned: options?.pin ?? existing?.pinned ?? false,
    });
    this.totalBytes += bytes;
    // Never evict what we were just asked to store, even unpinned: the caller
    // asked for it a microsecond ago and is the most likely next reader.
    this.trim(key);
  }

  /** Protect an entry from eviction while it is in use. */
  pin(key: string): void {
    const entry = this.entries.get(key);
    if (entry !== undefined) entry.pinned = true;
  }

  /** Release the protection and make the entry evictable again. */
  unpin(key: string): void {
    const entry = this.entries.get(key);
    if (entry === undefined) return;
    entry.pinned = false;
    // Unpinning is the moment an entry becomes a candidate, so this is where
    // an over-budget cache can finally come back down.
    this.trim();
  }

  delete(key: string): boolean {
    const entry = this.entries.get(key);
    if (entry === undefined) return false;
    this.entries.delete(key);
    this.totalBytes -= entry.bytes;
    this.options.onEvict?.(key, entry.value);
    return true;
  }

  clear(): void {
    for (const [key, entry] of this.entries) this.options.onEvict?.(key, entry.value);
    this.entries.clear();
    this.totalBytes = 0;
  }

  keys(): string[] {
    return [...this.entries.keys()];
  }

  /**
   * Evict least-recently-used unpinned entries until under budget.
   *
   * If everything left is pinned the cache stays over budget rather than
   * dropping something in use — going over is recoverable, a hole in the world
   * is not. Callers can watch `bytes` against `maxBytes` to notice.
   */
  private trim(protect?: string): void {
    if (this.totalBytes <= this.options.maxBytes) return;
    for (const [key, entry] of this.entries) {
      if (this.totalBytes <= this.options.maxBytes) return;
      if (entry.pinned || key === protect) continue;
      this.entries.delete(key);
      this.totalBytes -= entry.bytes;
      this.options.onEvict?.(key, entry.value);
    }
  }
}
